import React, { useEffect } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useUsageStats } from '../../context/UsageStatsContext';

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Color schemes matching the "Keep saving time" button
const colorSchemes = {
  // Purple for urgent (high/critical: ≤5 days)
  urgent: {
    background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    borderColor: '#7c3aed',
    primaryColor: '#7c3aed',
    secondaryColor: '#6d28d9',
    textColor: '#5b21b6',
    iconBgColor: 'rgba(124, 58, 237, 0.08)',
    iconHoverBgColor: 'rgba(124, 58, 237, 0.15)',
  },
  // Emerald green for safe (low/medium: >5 days)
  safe: {
    background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
    borderColor: '#10b981',
    primaryColor: '#10b981',
    secondaryColor: '#059669',
    textColor: '#047857',
    iconBgColor: 'rgba(16, 185, 129, 0.08)',
    iconHoverBgColor: 'rgba(16, 185, 129, 0.15)',
  },
};

type ColorScheme = 'urgent' | 'safe';

const getColorScheme = (daysRemaining?: number): ColorScheme => {
  if (daysRemaining === undefined) return 'safe';
  return daysRemaining <= 5 ? 'urgent' : 'safe';
};

// Highlighted container for empty state
// Colors match the "Keep saving time" button based on urgency
const StatsContainer = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== 'isCompact' && prop !== 'colorScheme',
})<{ isCompact?: boolean; colorScheme: ColorScheme }>(({ isCompact, colorScheme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: isCompact ? '10px' : '12px',
  padding: isCompact ? '6px 10px' : '16px 20px',
  margin: isCompact ? '0 0 4px 0' : '0 16px 8px 16px',
  borderRadius: isCompact ? '10px' : '12px',
  background: colorSchemes[colorScheme].background,
  border: `1px solid ${colorSchemes[colorScheme].borderColor}`,
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
    backgroundSize: '200% 100%',
    animation: `${shimmer} 3s ease-in-out infinite`,
    pointerEvents: 'none',
  },
}));

const StatsHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isCompact ? '6px' : '8px',
}));

const StatsTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCompact' && prop !== 'colorScheme',
})<{ isCompact?: boolean; colorScheme: ColorScheme }>(({ isCompact, colorScheme }) => ({
  fontSize: isCompact ? '0.85rem' : '0.9rem',
  fontWeight: 600,
  color: colorSchemes[colorScheme].textColor,
}));

const Divider = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'colorScheme',
})<{ colorScheme: ColorScheme }>(({ colorScheme }) => ({
  width: '100%',
  height: '1px',
  background: `linear-gradient(90deg, transparent, ${colorSchemes[colorScheme].borderColor}40, ${colorSchemes[colorScheme].borderColor}60, ${colorSchemes[colorScheme].borderColor}40, transparent)`,
}));

const StatsRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: isCompact ? '20px' : '24px',
  flexWrap: 'nowrap',
}));

const StatItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: isCompact ? '6px' : '6px',
}));

const StatValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCompact' && prop !== 'colorScheme',
})<{ isCompact?: boolean; colorScheme: ColorScheme }>(({ isCompact, colorScheme }) => ({
  fontSize: isCompact ? '1.05rem' : '1.1rem',
  fontWeight: 700,
  color: colorSchemes[colorScheme].secondaryColor,
}));

const StatLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isCompact' && prop !== 'colorScheme',
})<{ isCompact?: boolean; colorScheme: ColorScheme }>(({ isCompact, colorScheme }) => ({
  fontSize: isCompact ? '0.8rem' : '0.8rem',
  color: colorSchemes[colorScheme].primaryColor,
}));

// Compact icon button for active chat state (tooltip sparkles)
// Colors match the "Keep saving time" button based on urgency
const StatsIconButton = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'colorScheme',
})<{ colorScheme: ColorScheme }>(({ colorScheme }) => ({
  padding: '6px',
  color: colorSchemes[colorScheme].primaryColor,
  backgroundColor: colorSchemes[colorScheme].iconBgColor,
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: colorSchemes[colorScheme].iconHoverBgColor,
    transform: 'scale(1.1)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

const TooltipContent = styled(Box)({
  padding: '8px 4px',
});

const TooltipTitle = styled(Typography)({
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const TooltipStatRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
  '&:last-child': {
    marginBottom: 0,
  },
});

const TooltipStatValue = styled(Typography)({
  fontSize: '0.9rem',
  fontWeight: 700,
});

const TooltipStatLabel = styled(Typography)({
  fontSize: '0.8rem',
  opacity: 0.9,
});

interface UsageStatsDisplayProps {
  organizationId?: string;
  variant: 'empty' | 'compact';
  isCompact?: boolean;
  daysRemaining?: number;
}

export const UsageStatsDisplay: React.FC<UsageStatsDisplayProps> = ({
  organizationId,
  variant,
  isCompact = false,
  daysRemaining,
}) => {
  const { t } = useTranslation();
  const { usageStats: allUsageStats, fetchUsageStats } = useUsageStats();
  const colorScheme = getColorScheme(daysRemaining);

  useEffect(() => {
    if (organizationId) {
      fetchUsageStats(organizationId);
    }
  }, [organizationId, fetchUsageStats]);

  const usageStats = organizationId ? allUsageStats[organizationId] : null;

  // Don't render if no stats or no conversations
  if (!usageStats || usageStats.conversation_count === 0) {
    return null;
  }

  const formatTimeSavedExpanded = () => {
    const hours = usageStats.estimated_time_saved_hours;
    
    if (hours >= 1) {
      return t('usageStatsHours', { hours, count: hours });
    }
    return t('usageStatsLessThanHour');
  };

  const renderTooltipContent = () => (
    <TooltipContent>
      <TooltipTitle>
        <AutoAwesomeIcon sx={{ fontSize: '16px' }} />
        {t('usageStatsTitle')}
      </TooltipTitle>
      <TooltipStatRow>
        <AccessTimeIcon sx={{ fontSize: '16px', opacity: 0.8 }} />
        <TooltipStatValue>{formatTimeSavedExpanded()}</TooltipStatValue>
        <TooltipStatLabel>{t('usageStatsTimeSaved')}</TooltipStatLabel>
      </TooltipStatRow>
      <TooltipStatRow>
        <ChatBubbleOutlineIcon sx={{ fontSize: '16px', opacity: 0.8 }} />
        <TooltipStatValue>{usageStats.conversation_count}</TooltipStatValue>
        <TooltipStatLabel>{t('usageStatsConversations', { count: usageStats.conversation_count })}</TooltipStatLabel>
      </TooltipStatRow>
    </TooltipContent>
  );

  // Compact variant - icon with tooltip for active chat
  if (variant === 'compact') {
    return (
      <Tooltip
        title={renderTooltipContent()}
        placement="left"
        arrow
        PopperProps={{
          sx: { zIndex: 99999999 },
        }}
      >
        <StatsIconButton size="small" aria-label={t('usageStatsTitle')} colorScheme={colorScheme}>
          <AutoAwesomeIcon />
        </StatsIconButton>
      </Tooltip>
    );
  }

  // Empty state variant - highlighted box
  return (
    <StatsContainer
      isCompact={isCompact}
      colorScheme={colorScheme}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
    >
      <StatsHeader isCompact={isCompact}>
        <AutoAwesomeIcon sx={{ fontSize: isCompact ? '18px' : '20px', color: colorSchemes[colorScheme].primaryColor }} />
        <StatsTitle isCompact={isCompact} colorScheme={colorScheme}>{t('usageStatsTitle')}</StatsTitle>
      </StatsHeader>
      {isCompact && <Divider colorScheme={colorScheme} />}
      <StatsRow isCompact={isCompact}>
        <StatItem isCompact={isCompact}>
          <AccessTimeIcon sx={{ fontSize: isCompact ? '18px' : '18px', color: colorSchemes[colorScheme].secondaryColor }} />
          <StatValue isCompact={isCompact} colorScheme={colorScheme}>{formatTimeSavedExpanded()}</StatValue>
          <StatLabel isCompact={isCompact} colorScheme={colorScheme}>{t('usageStatsTimeSaved')}</StatLabel>
        </StatItem>
        <StatItem isCompact={isCompact}>
          <ChatBubbleOutlineIcon sx={{ fontSize: isCompact ? '18px' : '18px', color: colorSchemes[colorScheme].secondaryColor }} />
          <StatValue isCompact={isCompact} colorScheme={colorScheme}>{usageStats.conversation_count}</StatValue>
          <StatLabel isCompact={isCompact} colorScheme={colorScheme}>{t('usageStatsConversations', { count: usageStats.conversation_count })}</StatLabel>
        </StatItem>
      </StatsRow>
    </StatsContainer>
  );
};
